/**
 * Semantic component registry for Contract B (worker remote-runtime)
 * extensions.
 *
 * Worker-side plugin code references UI primitives by name — e.g.
 * `<DataTable />`, `<Drawer />`, `<CanvasSurface />`. The host owns rendering:
 * a {@link SemanticComponentRegistry} maps each name to a concrete component
 * type, and the Remote DOM host receiver consults the registry on every
 * worker-emitted mutation. The closed primitive vocabulary {@link CONTRACT_B_PRIMITIVES}
 * is intentionally narrow — adding a primitive is a coordinated host change so
 * plugins cannot extend the contract ad hoc.
 *
 * **Alignment with SDUI:** Where the Contract B name overlaps with the
 * `@ethisyscore/protocol` SDUI primitive vocabulary (`DataTable`, `Form`), the
 * names are identical to avoid synonym drift. SDUI's `Action` is intentionally
 * NOT reused — Contract B's interactive primitive is named `Button` so the
 * worker authoring API stays close to React DOM idioms. The two vocabularies
 * may converge as they evolve, but the host registries (Contract A SDUI vs
 * Contract B Semantic) remain distinct surfaces today.
 *
 * The registry is renderer-agnostic — the generic `TComponent` parameter is
 * whatever component type the host supplies (React `ComponentType` in
 * production; plain objects in tests). This keeps the registry free of a
 * React peer-dep requirement and lets tests assert behaviour without rendering.
 */

// ─── Closed v1 vocabulary ─────────────────────────────────────────────────────

/**
 * The mandatory Contract B (worker remote-runtime) semantic primitive vocabulary.
 *
 * Adding a primitive requires a coordinated host change + protocol bump —
 * plugins cannot extend this set. Each name has a host-supplied concrete
 * component (gogo-ui in production); the registry enforces 1:1 substitution.
 */
export const CONTRACT_B_PRIMITIVES = [
    "Button",
    "DataTable",
    "Form",
    "EntityPicker",
    "CommandBar",
    "Drawer",
    "Modal",
    "CanvasSurface",
    "WebGLSurface",
    // Phase 1 additions — mirror the vite-plugin's CONTRACT_B_SEMANTIC_PRIMITIVES
    // list verbatim; drift between the two is caught by the
    // contract-b.test.ts "import-map-allowlist matches extension-runtime
    // CONTRACT_B_PRIMITIVES" test.
    "Card",
    "Tabs",
    "Select",
    "Alert",
] as const;

/**
 * Union type of the Contract B semantic primitive names — exported so callers
 * (tests, hosts) get full IntelliSense and compile-time exhaustiveness checks.
 */
export type SemanticPrimitiveName = typeof CONTRACT_B_PRIMITIVES[number];

const KNOWN_PRIMITIVE_SET: ReadonlySet<string> = new Set(CONTRACT_B_PRIMITIVES);

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * Map of semantic primitive name → concrete component for Contract B
 * extensions.
 *
 * Construction is open-ended (callers register primitives one at a time) or
 * eager via {@link SemanticComponentRegistry.fromMap} which forces a complete
 * map up front and aborts if any primitive is missing.
 *
 * @typeParam TComponent - The host's concrete component shape. React hosts pass
 *                         `ComponentType<P>`; tests pass any tagged object.
 */
export class SemanticComponentRegistry<TComponent>
{
    private readonly entries = new Map<SemanticPrimitiveName, TComponent>();

    /**
     * Register a primitive → component binding.
     *
     * Throws if `name` is not in {@link CONTRACT_B_PRIMITIVES} (drift prevention)
     * or if `name` is already registered (silent-override prevention).
     */
    public register(name: SemanticPrimitiveName, component: TComponent): void
    {
        if (!KNOWN_PRIMITIVE_SET.has(name))
        {
            throw new Error(
                `[component-registry] unknown semantic primitive '${name}'. ` +
                `Allowed: ${CONTRACT_B_PRIMITIVES.join(", ")}.`,
            );
        }
        if (this.entries.has(name))
        {
            throw new Error(
                `[component-registry] primitive '${name}' is already registered. ` +
                `Construct a new registry instead of overriding a binding.`,
            );
        }
        this.entries.set(name, component);
    }

    /**
     * Resolve a primitive name to its concrete component. Throws if the name is
     * outside the Contract B vocabulary OR if the registry has no binding —
     * silent fallthrough would let typos render blank components, which is
     * worse than a fast failure for plugin authors.
     */
    public resolve(name: SemanticPrimitiveName): TComponent
    {
        if (!KNOWN_PRIMITIVE_SET.has(name))
        {
            throw new Error(
                `[component-registry] unknown semantic primitive '${name}'. ` +
                `Allowed: ${CONTRACT_B_PRIMITIVES.join(", ")}.`,
            );
        }
        const found = this.entries.get(name);
        if (found === undefined)
        {
            const registered = this.listRegistered().join(", ") || "<none>";
            throw new Error(
                `[component-registry] primitive '${name}' is not registered. ` +
                `Registered primitives: ${registered}.`,
            );
        }
        return found;
    }

    /**
     * Cheap presence probe — never throws. Use this when callers want to
     * fall back to a default component instead of erroring.
     */
    public has(name: SemanticPrimitiveName): boolean
    {
        return this.entries.has(name);
    }

    /**
     * Sorted list of registered primitive names. Stable output makes registry
     * diagnostics and snapshot tests deterministic.
     */
    public listRegistered(): SemanticPrimitiveName[]
    {
        return [...this.entries.keys()].sort() as SemanticPrimitiveName[];
    }

    /**
     * Build a registry from a complete `Record<SemanticPrimitiveName, T>` map.
     *
     * The `Record` type forces TypeScript to refuse incomplete literals at
     * compile time, AND we re-check at runtime so callers who type-erase the
     * record (e.g., via `as any`) still get a fast failure.
     */
    public static fromMap<T>(map: Record<SemanticPrimitiveName, T>): SemanticComponentRegistry<T>
    {
        const registry = new SemanticComponentRegistry<T>();
        const missing: SemanticPrimitiveName[] = [];
        for (const name of CONTRACT_B_PRIMITIVES)
        {
            const component = (map as Partial<Record<SemanticPrimitiveName, T>>)[name];
            if (component === undefined)
            {
                missing.push(name);
                continue;
            }
            registry.register(name, component);
        }
        if (missing.length > 0)
        {
            throw new Error(
                `[component-registry] fromMap is missing semantic primitive(s): ${missing.join(", ")}.`,
            );
        }
        return registry;
    }
}
